import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_CONFIG from '../../config/api';

// Define types
interface Operator {
  id: number;
  name: string;
  email: string;
  mobile: string;
  username: string;
  mainRole: string;
  active: boolean;
  lastLogin: string;
}

interface CurrentUser {
  _id: string;
  name: string;
  userType: string;
  mainRole?: string;
  email?: string;
}
interface NewOperator {
  name: string;
  email: string;
  mobile: string;
  username: string;
  mainRole: string;
  password: string;
  confirmPassword: string;
}
const Operators: React.FC = () => {
  const roleLabels: { [key: string]: string } = {
    SuperAdmin: 'سوپرادمین',
    Operator: 'پذیرشگر',
    Cashier: 'حسابدار',
    Supervisor: 'IT',
    Archive: 'جوابدهی'
  };
  const [operators, setOperators] = useState<Operator[]>([]);
  const [loading, setLoading] = useState(true);

  const [newOperator, setNewOperator] = useState({
    name: '',
    email: '',
    mobile: '',
    username: '',
    mainRole: '',
    password: '',
    confirmPassword: '',
  });

  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [editingOperatorId, setEditingOperatorId] = useState<number | null>(null);
  const [passwordValidation, setPasswordValidation] = useState<{
    length: boolean;
    hasNumber: boolean;
    hasLetter: boolean;
    passwordsMatch: boolean;
  }>({
    length: false,
    hasNumber: false,
    hasLetter: false,
    passwordsMatch: true
  });

  // Fetch current user data and operators when component mounts
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('authToken');
        if (!token) {
          setError('لطفا ابتدا وارد حساب کاربری خود شوید.');
          setLoading(false);
          return;
        }

        // Fetch current user data
        const userResponse = await axios.get(`${API_CONFIG.baseURL}/users/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (userResponse.data.success) {
          setCurrentUser(userResponse.data.data);
          
          // Check if user is admin before fetching operators
          if (userResponse.data.data.userType === 'Admin') {
            // Fetch operators data
            const operatorsResponse = await axios.get(`${API_CONFIG.baseURL}/admin/operators`, {
              headers: { Authorization: `Bearer ${token}` }
            });

            if (operatorsResponse.data.success) {
              setOperators(operatorsResponse.data.data);
            } else {
              setError('خطا در دریافت لیست اپراتورها');
            }
          } else {
            setError('شما دسترسی لازم برای مشاهده این بخش را ندارید');
          }
        }
      } catch (err) {

        setError('خطا در ارتباط با سرور');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setError('');
    
    const updatedOperator = {
      ...newOperator,
      [name]: value
    };
    
    // Update password validation state in real-time
    if (name === 'password') {
      const newPassword = value;
      const currentConfirmPassword = newOperator.confirmPassword;
      
      setPasswordValidation({
        length: newPassword.length >= 6,
        hasNumber: /\d/.test(newPassword),
        hasLetter: /[a-zA-Z]/.test(newPassword),
        passwordsMatch: newPassword === currentConfirmPassword && newPassword !== '' && currentConfirmPassword !== ''
      });
    } else if (name === 'confirmPassword') {
      const newConfirmPassword = value;
      const currentPassword = newOperator.password;
      
      setPasswordValidation({
        ...passwordValidation,
        passwordsMatch: newConfirmPassword === currentPassword && newConfirmPassword !== '' && currentPassword !== ''
      });
    }
    
    setNewOperator(updatedOperator);
  };

  // Password validation function
  const validatePassword = (password: string): string | null => {
    if (password.length < 6) {
      return 'رمز عبور باید حداقل ۶ کاراکتر باشد';
    }
    
    const hasNumber = /\d/.test(password);
    const hasLetter = /[a-zA-Z]/.test(password);
    
    if (!hasNumber) {
      return 'رمز عبور باید شامل حداقل یک عدد باشد';
    }
    
    if (!hasLetter) {
      return 'رمز عبور باید شامل حداقل یک حرف باشد';
    }
    
    return null;
  };

  const handleAddOperator = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // If editing, call update API
    if (editingOperatorId) {
      try {
        const token = localStorage.getItem('authToken');
        if (!token) {
          setError('لطفا ابتدا وارد حساب کاربری خود شوید.');
          setIsLoading(false);
          return;
        }
        // Prepare update data
        const updateData: any = {
          name: newOperator.name,
          email: newOperator.email,
          mobile: newOperator.mobile,
          username: newOperator.username,
          mainRole: newOperator.mainRole
        };
        
        // Include password only if provided
        if (newOperator.password) {
          updateData.password = newOperator.password;
        }
        
        const response = await axios.put(
          `${API_CONFIG.baseURL}/admin/operators/${editingOperatorId}`,
          updateData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data.success) {
          setOperators(operators.map(op =>
            op.id === editingOperatorId
              ? { ...op, name: newOperator.name, email: newOperator.email, mobile: newOperator.mobile, username: newOperator.username, mainRole: newOperator.mainRole }
              : op
          ));
          setShowAddForm(false);
          setEditingOperatorId(null);
          setNewOperator({ name: '', email: '', mobile: '', username: '', mainRole: '', password: '', confirmPassword: '' });
          setPasswordValidation({ length: false, hasNumber: false, hasLetter: false, passwordsMatch: true });
        } else {
          setError(response.data.message || 'خطا در بروزرسانی اپراتور');
        }
      } catch (err: any) {

        setError(err.response?.data?.message || 'خطا در ارتباط با سرور');
      } finally {
        setIsLoading(false);
      }
      return;
    }
    
    // Validate password strength (for new operators or when editing with new password)
    if (newOperator.password) {
      const passwordError = validatePassword(newOperator.password);
      if (passwordError) {
        setError(passwordError);
        setIsLoading(false);
        return;
      }
    } else if (!editingOperatorId) {
      // Password is required for new operators
      setError('رمز عبور الزامی است');
      setIsLoading(false);
      return;
    }

    // Check password confirmation (only if password is provided)
    if (newOperator.password) {
      if (!newOperator.confirmPassword) {
        setError('لطفاً تکرار رمز عبور را وارد کنید.');
        setIsLoading(false);
        return;
      }
      if (newOperator.password !== newOperator.confirmPassword) {
        setError('رمز عبور و تکرار آن مطابقت ندارند.');
        setIsLoading(false);
        return;
      }
    }
    
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setError('لطفا ابتدا وارد حساب کاربری خود شوید.');
        setIsLoading(false);
        return;
      }
      
      // Check if current user is SuperAdmin to include the accept parameter
      const isSuperAdmin = currentUser && currentUser.mainRole === 'SuperAdmin';
      
      // Prepare data for API
      const operatorData = {
        name: newOperator.name,
        email: newOperator.email,
        mobile: newOperator.mobile,
        username: newOperator.username,
        mainRole: newOperator.mainRole,
        password: newOperator.password,
        userType: 'Admin', // Set user type to Admin
        // Include accept parameter if user is SuperAdmin
        ...(isSuperAdmin ? { accept: true } : {})
      };
      
      // Make API call to create new operator
      const response = await axios.post(
        `${API_CONFIG.baseURL}/admin/operators`, 
        operatorData, 
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      if (response.data.success) {
        // Add the new operator to the local state
        const newId = operators.length > 0 
          ? Math.max(...operators.map(op => op.id)) + 1
          : 1;
        
        const newOperatorData: Operator = {
          id: newId,
          name: newOperator.name,
          email: newOperator.email,
          mobile: newOperator.mobile,
          username: newOperator.username,
          mainRole: newOperator.mainRole,
          active: true,
          lastLogin: '-'
        };
        
        setOperators([...operators, newOperatorData]);
        
        // Reset form
        setNewOperator({
          name: '',
          email: '',
          mobile: '',
          username: '',
          mainRole: '',
          password: '',
          confirmPassword: ''
        });
        setPasswordValidation({ length: false, hasNumber: false, hasLetter: false, passwordsMatch: true });
        setShowAddForm(false);
      } else {
        setError(response.data.message || 'خطا در ثبت اپراتور جدید');
      }
    } catch (err: any) {

      // Handle API error messages
      if (err.response && err.response.data) {
        setError(err.response.data.message || 'خطا در ثبت اپراتور جدید');
      } else {
        setError('خطا در ارتباط با سرور');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const toggleOperatorStatus = async (id: number) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setError('لطفا ابتدا وارد حساب کاربری خود شوید.');
        return;
      }

      // Find the operator to toggle their status
      const operator = operators.find(op => op.id === id);
      if (!operator) return;

      const newStatus = !operator.active;
      
      // Make API call to update operator status
      const response = await axios.put(
        `${API_CONFIG.baseURL}/admin/operators/${id}/status`, 
        { active: newStatus },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      if (response.data.success) {
        // Update the local state to reflect the change
        setOperators(operators.map(op => 
          op.id === id ? { ...op, active: newStatus } : op
        ));
      } else {
        setError(response.data.message || 'خطا در تغییر وضعیت اپراتور');
      }
    } catch (err) {

      setError('خطا در ارتباط با سرور');
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">اپراتورهای سامانه</h1>
          <button 
            onClick={() => {
              setShowAddForm(!showAddForm);
              if (showAddForm) {
                // Reset form when cancelling
                setNewOperator({ name: '', email: '', mobile: '', username: '', mainRole: '', password: '', confirmPassword: '' });
                setPasswordValidation({ length: false, hasNumber: false, hasLetter: false, passwordsMatch: true });
                setEditingOperatorId(null);
                setError('');
              }
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {showAddForm ? 'انصراف' : 'افزودن اپراتور جدید'}
          </button>
        </div>
        
        {showAddForm && (
          <div className="mb-8 bg-gray-50 p-4 rounded-lg">
            <h2 className="text-lg font-semibold mb-4">
              {editingOperatorId ? 'ویرایش اپراتور' : 'افزودن اپراتور جدید'}
            </h2>
            
            {!editingOperatorId && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <h3 className="text-sm font-medium text-blue-800 mb-1">الزامات رمز عبور:</h3>
                <ul className="text-xs text-blue-700 space-y-1">
                  <li>• حداقل ۶ کاراکتر</li>
                  <li>• شامل حداقل یک حرف (a-z یا A-Z)</li>
                  <li>• شامل حداقل یک عدد (0-9)</li>
                  <li>• رمز عبور و تکرار آن باید مطابقت داشته باشند</li>
                </ul>
              </div>
            )}
            
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-600">
                {error}
              </div>
            )}
            <form onSubmit={handleAddOperator}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">نام و نام خانوادگی</label>
                  <input
                    type="text"
                    name="name"
                    value={newOperator.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">ایمیل</label>
                  <input
                    type="email"
                    name="email"
                    value={newOperator.email}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">موبایل</label>
                  <input
                    type="text"
                    name="mobile"
                    value={newOperator.mobile}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">نام کاربری <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    name="username"
                    value={newOperator.username}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">نقش</label>
                  <select
                    name="mainRole"
                    value={newOperator.mainRole}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">انتخاب کنید</option>
                    <option value="SuperAdmin">سوپرادمین</option>
                    <option value="Operator">پذیرشگر</option>
                    <option value="Cashier">حسابدار</option>
                    <option value="Supervisor">IT</option>
                    <option value="Archive">جوابدهی</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    رمز عبور {!editingOperatorId && <span className="text-red-500">*</span>}
                    {editingOperatorId && <span className="text-xs text-gray-500 mr-2">(اختیاری)</span>}
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={newOperator.password}
                    onChange={handleInputChange}
                    required={!editingOperatorId}
                    placeholder={editingOperatorId ? "برای عدم تغییر خالی بگذارید" : "حداقل ۶ کاراکتر شامل حرف و عدد"}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                  {newOperator.password && (
                    <div className="mt-2 space-y-1">
                      <div className={`flex items-center text-xs ${passwordValidation.length ? 'text-green-600' : 'text-red-600'}`}>
                        <span className="ml-1">{passwordValidation.length ? '✓' : '✗'}</span>
                        حداقل ۶ کاراکتر
                      </div>
                      <div className={`flex items-center text-xs ${passwordValidation.hasLetter ? 'text-green-600' : 'text-red-600'}`}>
                        <span className="ml-1">{passwordValidation.hasLetter ? '✓' : '✗'}</span>
                        شامل حداقل یک حرف
                      </div>
                      <div className={`flex items-center text-xs ${passwordValidation.hasNumber ? 'text-green-600' : 'text-red-600'}`}>
                        <span className="ml-1">{passwordValidation.hasNumber ? '✓' : '✗'}</span>
                        شامل حداقل یک عدد
                      </div>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    تکرار رمز عبور {(!editingOperatorId || newOperator.password) && <span className="text-red-500">*</span>}
                  </label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={newOperator.confirmPassword}
                    onChange={handleInputChange}
                    required={!editingOperatorId || !!newOperator.password}
                    placeholder={editingOperatorId ? "برای عدم تغییر خالی بگذارید" : "تکرار رمز عبور"}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                  {(newOperator.password && newOperator.confirmPassword) && (
                    <div className="mt-2">
                      <div className={`flex items-center text-xs ${passwordValidation.passwordsMatch ? 'text-green-600' : 'text-red-600'}`}>
                        <span className="ml-1">{passwordValidation.passwordsMatch ? '✓' : '✗'}</span>
                        رمز عبور و تکرار آن مطابقت دارند
                      </div>
                    </div>
                  )}
                  {(newOperator.password || newOperator.confirmPassword) && !(newOperator.password && newOperator.confirmPassword) && (
                    <div className="mt-2">
                      <div className="flex items-center text-xs text-gray-500">
                        <span className="ml-1">•</span>
                        لطفاً هر دو فیلد رمز عبور را پر کنید
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-4 flex justify-end">
                <button
                  type="submit"
                  disabled={(() => {
                    if (isLoading) return true;
                    if (!newOperator.username) return true; // Username is required
                    if (!editingOperatorId && !newOperator.password) return true;
                    if (newOperator.password && (!passwordValidation.length || !passwordValidation.hasNumber || !passwordValidation.hasLetter)) return true;
                    if (newOperator.password && newOperator.confirmPassword && !passwordValidation.passwordsMatch) return true;
                    if (newOperator.password && !newOperator.confirmPassword) return true;
                    if (!newOperator.password && newOperator.confirmPassword) return true;
                    return false;
                  })()}
                  className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'در حال پردازش...' : (editingOperatorId ? 'بروزرسانی اپراتور' : 'ثبت اپراتور')}
                </button>
              </div>
            </form>
          </div>
        )}
        
        {loading ? (
          <div className="text-center py-4">
            <svg className="animate-spin h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border border-gray-200">
              <thead>
                <tr className="bg-gray-100">
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">شناسه</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">نام و نام خانوادگی</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">نام کاربری</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">ایمیل</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">موبایل</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">نقش</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">وضعیت</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">آخرین ورود</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">عملیات</th>
                </tr>
              </thead>
              <tbody>
                {operators.map((op: Operator) => (
                  <tr key={op.id} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm">{op.id.toString().slice(-8)}</td>
                    <td className="py-3 px-4 text-sm">{op.name}</td>
                    <td className="py-3 px-4 text-sm">{op.username}</td>
                    <td className="py-3 px-4 text-sm">{op.email}</td>
                    <td className="py-3 px-4 text-sm">{op.mobile}</td>
                    <td className="py-3 px-4 text-sm">{roleLabels[op.mainRole] || op.mainRole}</td>
                    <td className="py-3 px-4 text-sm">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${op.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {op.active ? 'فعال' : 'غیرفعال'}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm">{op.lastLogin}</td>
                    <td className="py-3 px-4 text-sm">
                      <button
                        onClick={() => toggleOperatorStatus(op.id)}
                        className={`mr-2 px-3 py-1 rounded text-xs ${op.active ? 'bg-red-100 text-red-700 hover:bg-red-200' : 'bg-green-100 text-green-700 hover:bg-green-200'}`}
                      >
                        {op.active ? 'غیرفعال کردن' : 'فعال کردن'}
                      </button>
                      <button
                        onClick={() => {
                          setShowAddForm(true);
                          setEditingOperatorId(op.id);
                          setNewOperator({
                            name: op.name,
                            email: op.email,
                            mobile: op.mobile,
                            username: op.username,
                            mainRole: op.mainRole,
                            password: '',
                            confirmPassword: ''
                          });
                          setPasswordValidation({ length: false, hasNumber: false, hasLetter: false, passwordsMatch: true });
                          setError('');
                        }}
                        className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200"
                      >
                        ویرایش
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Operators;
